import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Users, Target, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClubPage = () => {
  const [direction, setDirection] = useState([]);

  useEffect(() => {
    fetchDirection();
  }, []);

  const fetchDirection = async () => {
    try {
      const response = await axios.get(`${API}/direction`);
      setDirection(response.data);
    } catch (error) {
      console.error('Error fetching direction:', error);
    }
  };

  const milestones = [
    { year: '1979', event: 'Fundação do GDR Esporões' },
    { year: '1991/92', event: 'Presença na Taça de Portugal' },
    { year: '1992/93', event: 'Campeão Distrital 2ª Divisão AF Braga' },
    { year: '2019/20', event: 'Acesso à Divisão de Honra' },
    { year: '2020/21', event: 'Campeão e subida à Pro-Nacional' },
    { year: '2024', event: 'Projeto "Gverreiros do Futuro" com SC Braga' },
  ];

  const values = [
    { icon: Trophy, title: 'Determinação', desc: 'Orgulho e determinação em crescer e evoluir constantemente.' },
    { icon: Users, title: 'Comunidade', desc: 'Uma freguesia que nunca deixou de acreditar no seu clube.' },
    { icon: Target, title: 'Superação', desc: 'Renascer das cinzas, um percurso tido como impossível.' },
    { icon: Heart, title: 'Paixão', desc: 'O futuro escreve-se com a mesma paixão que nos trouxe até aqui.' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="club-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">GDR Esporões</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">O CLUBE</h1>
        </div>
      </section>

      {/* History Section */}
      <section className="py-24 section-padding" data-testid="history-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Desde 1979</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black mt-2 mb-6">A NOSSA HISTÓRIA</h2>
              <div className="space-y-6 text-gray-400 leading-relaxed">
                <p>
                  O Grupo Desportivo e Recreativo de Esporões foi fundado a 27 de novembro de 1979, em Esporões – Braga. Após uma suspensão de atividade de dezassete anos, o clube renasceu há quase duas décadas.
                </p>
                <p>
                  No primeiro ano deste regresso, o foco foi reconstruir a base, reunir novos sócios, parcerias e condições para devolver o GDR Esporões à ribalta.
                </p>
                <p>
                  A história do clube está intrinsecamente ligada ao futebol, com marcos importantes como a presença na Taça de Portugal em 1991/1992, o título de campeão distrital da 2ª Divisão da AF Braga em 1992/1993, e mais recentemente, o acesso à Divisão de Honra em 2019/2020.
                </p>
                <p>
                  Em 2020/2021, o clube sagrou-se campeão e subiu à Pro-Nacional, a maior divisão da AF Braga. Atualmente, o GDR Esporões disputa a 1ª Divisão da Associação de Futebol de Braga.
                </p>
                <p>
                  O clube também lançou um projeto de formação em parceria com o SC Braga, os "Gverreiros do Futuro de Esporões", com cerca de 80 crianças a treinar e competir.
                </p>
              </div>
            </div>
            <div>
              <div className="bg-[#0A0A0A] border border-white/10 p-8">
                <h3 className="font-heading text-2xl font-bold mb-8">MARCOS HISTÓRICOS</h3>
                <div className="space-y-6 border-l border-white/20 pl-6 relative">
                  {milestones.map((item, index) => (
                    <div key={index} className="timeline-item relative">
                      <span className="font-mono text-sm text-gray-500">{item.year}</span>
                      <p className="text-white mt-1">{item.event}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-[#0A0A0A] border-y border-white/10" data-testid="values-section">
        <div className="container-custom section-padding">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">O Que Nos Define</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-2">MISSÃO E VALORES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((item, index) => (
              <div key={index} className="border border-white/10 p-8 hover:border-white/30 transition-colors">
                <item.icon size={32} className="mb-4 text-white" />
                <h3 className="font-heading text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 section-padding" data-testid="mission-section">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">A Nossa Missão</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-2 mb-8">
              LEVAR O NOME DE ESPORÕES AO MAIS ALTO
            </h2>
            <p className="text-gray-400 leading-relaxed text-lg">
              Promover a prática desportiva, com foco particular no futebol, desenvolvendo um projeto de formação para crianças e jovens. Fortalecer a ligação à comunidade de Esporões e continuar a escrever a história do clube com a mesma paixão e dedicação que nos trouxe até aqui.
            </p>
          </div>
        </div>
      </section>

      {/* Direction Section */}
      <section className="py-24 bg-[#0A0A0A] border-y border-white/10" data-testid="direction-section">
        <div className="container-custom section-padding">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Liderança</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-2">DIREÇÃO</h2>
          </div>
          {direction.length > 0 ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {direction.map((member) => (
                <div key={member.id} className="border border-white/10 p-6 text-center hover:border-white/30 transition-colors">
                  <div className="w-20 h-20 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="font-heading font-bold text-2xl text-gray-400">
                        {member.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-lg">{member.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{member.role}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10">
              <p className="text-gray-500">Informação da direção em breve disponível</p>
            </div>
          )}
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-24 section-padding" data-testid="facilities-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Informação</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black mt-2 mb-6">SEDE E INSTALAÇÕES</h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                A sede do GDR Esporões está localizada na Rua José Loureiro Duarte, n.º 2, em Esporões – Braga.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Atualmente, o clube não dispõe de infraestruturas desportivas próprias, utilizando instalações cedidas pela comunidade para os seus treinos e jogos em casa.
              </p>
              <div className="bg-[#0A0A0A] border border-white/10 p-6">
                <h4 className="font-heading font-bold mb-4">EQUIPAS EM COMPETIÇÃO</h4>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    Seniores Masculinos - 1ª Divisão AF Braga
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    Seniores Femininos - 1ª Divisão Futsal Feminino AF Braga
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    Gverreiros do Futuro - Formação (parceria SC Braga)
                  </li>
                </ul>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video bg-[#0A0A0A] border border-white/10 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80"
                  alt="Campo de futebol"
                  className="w-full h-full object-cover img-grayscale"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClubPage;
